/** A time typed: `8:15`, `08.15` or `0815` read as `08:15`; empty is none; `undefined` when wrong. */
export function timeOf(text: string): string | null | undefined {
  const typed = text.trim();
  if (typed === '') return null;
  const found = /^(\d{1,2})[:.]?(\d{2})$/.exec(typed);
  if (found === null) return undefined;
  const [, hours = '', minutes = ''] = found;
  return Number(hours) < 24 && Number(minutes) < 60
    ? `${hours.padStart(2, '0')}:${minutes}`
    : undefined;
}

/** A link typed: none when empty, `undefined` when it is no http(s) address. */
export function linkOf(text: string): string | null | undefined {
  const typed = text.trim();
  if (typed === '') return null;
  return /^https?:\/\/\S+$/.test(typed) ? typed : undefined;
}

/** Each zone's reader, made once: making one is slow, and a timeline near reads thousands. */
const readers = new Map<string, Intl.DateTimeFormat>();

/** An instant's wall clock in a zone, read as if it were UTC: its offset is the difference. */
export function wall(zone: string, at: number): number {
  const reader =
    readers.get(zone) ??
    new Intl.DateTimeFormat('sv-SE', {
      timeZone: zone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    });
  readers.set(zone, reader);
  const shown = reader.format(new Date(at));
  return Date.parse(`${shown.replace(' ', 'T')}Z`);
}

/** The instant a wall clock `time` on `day` is in `zone`: its offset then, asked twice across a
 * change of the clocks. */
function instant(day: string, time: string, zone: string | null): number {
  const naive = Date.parse(`${day}T${time}:00Z`);
  if (zone === null) return naive;
  const guess = naive - (wall(zone, naive) - naive);
  return naive - (wall(zone, guess) - guess);
}

/**
 * How many days after it leaves a way arrives, when both times are said, each local at its
 * place: the first day it arrives after it left — the one nearest to how long it takes, when
 * that is known. Singapore 23:00 to Sydney 09:00 is `1`; a flight over the date line may be two.
 */
export function daysLater(
  day: string,
  leaves: string,
  arrives: string,
  minutes: number | null,
  from: string | null,
  to: string | null,
): number {
  const left = instant(day, leaves, from ?? to);
  const after = [0, 1, 2, 3].filter(
    (days) => instant(later(day, days), arrives, to ?? from) >= left,
  );
  const took = (days: number) => instant(later(day, days), arrives, to ?? from) - left;
  return minutes === null
    ? Math.min(...after)
    : after.reduce((best, days) =>
        Math.abs(took(days) - minutes * 60_000) < Math.abs(took(best) - minutes * 60_000)
          ? days
          : best,
      );
}

/** `2026-10-18` and 2 days on: `2026-10-20`. */
export function later(day: string, days: number): string {
  return new Date(Date.parse(`${day}T00:00:00Z`) + days * 86_400_000)
    .toISOString()
    .slice(0, 10);
}

/**
 * When a way arrives, about: it leaves `day` at `leaves` local at its start, in zone `from`,
 * takes `minutes`, and arrives local at its end, in zone `to` — `11:35`, a day later `+1`. A
 * place with no zone known keeps the other's.
 */
export function arrival(
  day: string,
  leaves: string,
  minutes: number,
  from: string | null,
  to: string | null,
): string {
  const naive = Date.parse(`${day}T${leaves}:00Z`);
  const start = from ?? to;
  const end = to ?? from;
  // The leaving wall clock as an instant: its zone's offset then, asked twice across a change.
  const guess = start === null ? naive : naive - (wall(start, naive) - naive);
  const left = start === null ? naive : naive - (wall(start, guess) - guess);
  const arrived = left + minutes * 60_000;
  const local = end === null ? arrived : wall(end, arrived);
  const clock = new Date(local).toISOString().slice(11, 16);
  const days = Math.round(
    (Date.parse(`${new Date(local).toISOString().slice(0, 10)}T00:00:00Z`) -
      Date.parse(`${day}T00:00:00Z`)) /
      86_400_000,
  );
  return days > 0 ? `${clock} +${String(days)}` : clock;
}

/** An instant as kept, `2026-10-28T16:00:00Z`: a wall clock `time` on `day` in `zone`. */
export function instantOf(day: string, time: string, zone: string | null): string {
  return `${new Date(instant(day, time, zone)).toISOString().slice(0, 19)}Z`;
}

/** An instant's wall clock in a zone: its day and its time; without a zone, in UTC. */
export function wallOf(at: string, zone: string | null): { day: string; time: string } {
  const moment = Date.parse(at);
  const shown = new Date(zone === null ? moment : wall(zone, moment)).toISOString();
  return { day: shown.slice(0, 10), time: shown.slice(11, 16) };
}

/**
 * When a way arrives, as an instant, from the clock time it arrives at its end: the day after it
 * leaves that is nearest to how long it takes — Singapore 23:00 to Sydney 06:10 is the next day.
 */
export function arrivalAt(
  left: string,
  arrives: string,
  minutes: number | null,
  from: string | null,
  to: string | null,
): string {
  const leaving = wallOf(left, from ?? to);
  const days = daysLater(leaving.day, leaving.time, arrives, minutes, from, to);
  return instantOf(later(leaving.day, days), arrives, to ?? from);
}

/** How many days after it leaves, local at each end, a way arrives: `+1` is shown. */
export function daysAfter(
  left: string,
  arrived: string,
  from: string | null,
  to: string | null,
): number {
  const day = (iso: string) => Date.parse(`${iso}T00:00:00Z`);
  return Math.round(
    (day(wallOf(arrived, to ?? from).day) - day(wallOf(left, from ?? to).day)) / 86_400_000,
  );
}

/** How long, from minutes: `14 h 35 min`, `2 h` on the hour, or `50 min`. */
export function minutesSaid(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours === 0) return `${String(rest)} min`;
  return rest === 0
    ? `${String(hours)} h`
    : `${String(hours)} h ${String(rest).padStart(2, '0')} min`;
}

/** How long from one instant to another, said as `minutesSaid` says it. */
export function lasting(left: string, arrived: string): string {
  return minutesSaid(Math.round((Date.parse(arrived) - Date.parse(left)) / 60_000));
}
